import { Autocomplete, Box, Checkbox, CircularProgress, TextField, Tooltip } from '@mui/material';
import { useState, FC, useEffect, useCallback } from 'react';
import styles from './InsuranceContracts.module.css';
import CurrencyRubleIcon from '@mui/icons-material/CurrencyRuble';
import TuneIcon from '@mui/icons-material/Tune';
import { StyledBottomNavigation, StyledBottomNavigationAction } from 'features/insurance-contracts/ui/StyledComponents';
import { useAppDispatch, useAppSelector } from 'shared/utils/hooks/useRedux';
import List from 'widgets/list/list';
import ModalWidget from 'widgets/modal/Modal';
import { ContractsList, CurrentPageContractsList, Download, ErrorDownload, FileData, FileName, LoadingContractsList } from 'entities/insurance-contscts/selector';
import { fetchContractList, fetchExelDownload } from 'entities/insurance-contscts/actions/fetch-contracts-list';
import { resetFileData, setCurrentPageInsuranceContracts } from 'entities/insurance-contscts';
import { IList } from 'entities/insurance-contscts/types/InsuranseContracts-interface';
import SearchIcon from '@mui/icons-material/Search';

import appStyles from 'app/providers/theme/App.module.css';
import Loader from 'shared/loader/Loader';
import SettingsIcon from '@mui/icons-material/Settings';
import { allFields, defaultFields, fieldMappings } from 'features/insurance-contracts/utils/constant';
import TransferList from 'widgets/field-settings/FieldSettings';
import CustomButton from 'shared/custom-button/CustomButton';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { AlertMessage } from 'shared/alert-message/alert-message';

interface IAlertInfo {
    message?: string;
    severity?: string;
}

export const InsuranceContracts: FC = () => {
    const dispatch = useAppDispatch();
    const [value, setValue] = useState(true);
    const [sortArray, setSortArray] = useState<string[]>([]);
    const listData = useAppSelector(ContractsList);
    const currentPage = useAppSelector(CurrentPageContractsList);
    const isLoading = useAppSelector(LoadingContractsList);
    const [open, setOpen] = useState(false);
    const [openSearch, setOpenSearch] = useState(false);

    const [searchQuery, setSearchQuery] = useState<string>('');

    const handleOpenFilter = () => setOpen((prevState) => !prevState);

    const handleOpenSearch = () => setOpenSearch((prevState) => !prevState);

    useEffect(() => {
        dispatch(setCurrentPageInsuranceContracts({ page: 1 }));
        dispatch(fetchContractList({ value, sortArray }));
    }, [dispatch, value, sortArray]);

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        dispatch(setCurrentPageInsuranceContracts({ page: 1 }));
    }, [dispatch]);

    const handleChildAction = useCallback((page: number) => {
        dispatch(setCurrentPageInsuranceContracts({ page }));
    }, [dispatch]);

    const [selectedProductNames, setSelectedProductNames] = useState<string[]>([]);
    const filteredData = listData.filter((item: IList) => {
        const matchesSearchQuery =
            item.Number.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.InsurerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.ConclusionDate.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.PaymentDate.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.ProductName.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesProductName =
            selectedProductNames.length === 0 ||
            selectedProductNames.includes(item.ProductName);

        return matchesSearchQuery && matchesProductName;
    });


    const paginatedData = () => {
        const startIndex = (currentPage - 1) * 10;
        return filteredData.slice(startIndex, startIndex + 10);
    };

    const [openSetting, setOpenSetting] = useState(false);

    const [fields, setFields] = useState<string[]>(defaultFields);
    const [tempFields, setTempFields] = useState<string[]>(fields);

    const handleOpenSetting = () => {
        setTempFields(fields);
        setOpenSetting(true);
    };

    const handleCloseSetting = () => {
        setOpenSetting(false);
    };
    const handleResetSetting = () => {
        setTempFields(defaultFields);
    };
    const handleAcceptSetting = () => {
        setFields(tempFields);
        setOpenSetting(false);
    };

    const handleTempFieldsChange = (newFields: string[]) => {
        setTempFields(newFields);
    };
    const fileData = useAppSelector(FileData);
    const fileName = useAppSelector(FileName);
    const isDownload = useAppSelector(Download);
    const errorDownload = useAppSelector(ErrorDownload);
    const [alertInfo, setAlertInfo] = useState<IAlertInfo>({ message: '', severity: '' });
    const [downloadTriggered, setDownloadTriggered] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);


    const handleFileDownload = async () => {
        setAlertInfo({ message: '', severity: '' });

        try {
            setIsDownloading(true);

            await dispatch(fetchExelDownload());

            setDownloadTriggered(true);
        } catch (error) {
            console.error('Error downloading the file:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    useEffect(() => {
        if (downloadTriggered && fileData && errorDownload.length === 0) {
            const link = document.createElement('a');
            link.href = `data:application/octet-stream;base64,${fileData}`;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            dispatch(resetFileData());
            setDownloadTriggered(false);
        } else if (errorDownload.length > 0) {
            setAlertInfo({ message: errorDownload[0].Msg, severity: 'error' });
            const timeoutId = setTimeout(() => {
                setAlertInfo({ message: '', severity: '' });
                dispatch(resetFileData());
            }, 3000);
            return () => clearTimeout(timeoutId);
        }
    }, [downloadTriggered, fileData, errorDownload, fileName, dispatch]);

    const renderItem = (item: any) => (
        <>
            {fields.map((field) => {
                const key = fieldMappings[field];
                if (key === 'InsurancePremium') {
                    return (
                        <Box key={field}>
                            <p>{item.InsurancePremium} <span className={styles.icon}><CurrencyRubleIcon /></span></p>
                        </Box>
                    );
                } else {
                    return (
                        <Box key={field}>
                            <p>{item[key]}</p>
                        </Box>
                    );
                }
            })}
        </>
    );
    return (
        <>
            <Box className={appStyles.titleBox}>
                <h1>Договоры страхования</h1>
                <Box className={styles.rightBox}>
                    <StyledBottomNavigation
                        width="400px"
                        value={value ? 0 : 1}
                        onChange={(_, newValue) => {
                            if (!isLoading) {
                                setValue(newValue === 0)
                            }
                        }}
                        showLabels
                    >
                        <StyledBottomNavigationAction
                            custommaxwidth="250px"
                            data-isactive={value}
                            data-isleft={true}
                            data-isright={false}
                            label="Действующие"
                        />
                        <StyledBottomNavigationAction
                            custommaxwidth="250px"
                            data-isactive={!value}
                            data-isleft={false}
                            data-isright={true}
                            label="Архив"
                        />
                    </StyledBottomNavigation>
                </Box>
            </Box>
            {isLoading && (
                <Loader
                    title='Подготовка списка. Это займет несколько секунд.'
                />
            )}
            {!isLoading && (
                <>
                    {alertInfo.message && (
                        <Box className={appStyles.alertBox}>
                            <AlertMessage alertInfo={alertInfo} />
                        </Box>
                    )}

                    {listData.length > 0 && (
                        <Box className={styles.btnContainer}>
                            {isDownload ? (
                                <CircularProgress color="inherit" size={20} />
                            ) : (
                                <Tooltip title="Выгрузить в Excel" placement="top">
                                    <Box>
                                        <CustomButton
                                            icon={FileDownloadIcon}
                                            handleClick={handleFileDownload}
                                            textColor='white'
                                            backgroundColor="#0054E6"
                                            border='none'
                                            borderRadius='4px'
                                            padding="8px 30px"
                                            disabled={false}
                                        />
                                    </Box>
                                </Tooltip>
                            )}
                        </Box>
                    )}
                    <Box className={appStyles.iconSort}>
                        <span
                            onClick={() => handleOpenSetting()}
                        >
                            <SettingsIcon />
                        </span>
                        <span onClick={handleOpenSearch} className={`${openSearch ? appStyles.iconSelected : ''}`}><SearchIcon /></span>
                        <span onClick={handleOpenFilter} className={`${open ? appStyles.iconSelected : ''}`}><TuneIcon /></span>
                    </Box>
                    {openSearch && (
                        <TextField
                            label="Поиск"
                            variant="outlined"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            fullWidth
                            margin="normal"
                        />
                    )}
                    {open && (
                        <Box className={styles.ProductName}>
                            <Autocomplete
                                multiple
                                options={Array.from(new Set(listData.map((item: IList) => item.ProductName)))}
                                value={selectedProductNames}
                                onChange={(event, newValue) => {
                                    setSelectedProductNames(newValue);
                                }}
                                renderOption={(props, option, { selected }) => {
                                    const { key, ...restProps } = props;
                                    return (
                                        <li
                                            key={option}
                                            {...restProps}
                                        >
                                            <Checkbox
                                                checked={selected}
                                            />
                                            {option}
                                        </li>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Фильтр по виду страхования"
                                        placeholder="Выберите вид страхования"
                                    />
                                )}
                            />
                        </Box>
                    )}

                    <ModalWidget
                        open={openSetting}
                        handleClose={handleCloseSetting}
                        title="Настройка полей таблицы"
                        closeIconPosition="right"
                        showAcceptButton
                        showCancelButton
                        onAccept={handleAcceptSetting}
                        onCancel={handleResetSetting}
                        widthProps={650}
                    >
                        <TransferList
                            key={JSON.stringify(tempFields)}
                            defaultFields={tempFields}
                            allFields={allFields}
                            onFieldsChange={handleTempFieldsChange}
                        />
                    </ModalWidget>

                    {listData.length > 0 && filteredData.length > 0 ? (
                        <Box sx={{ width: '100%', overflow: 'hidden', position: 'relative' }}>

                            <List
                                title={fields}
                                data={paginatedData()}
                                renderItem={renderItem}
                                renderPagination={filteredData.length > 10}
                                pageCount={Math.ceil(filteredData.length / 10)}
                                onAction={handleChildAction}
                                currentPage={currentPage}
                                pathnameProps="/insurance-contracts/case/"
                                link={true}
                            />
                        </Box>
                    ) : (
                        <Box className={appStyles.infoBox}>
                            <p>Договоры страхования не найдены</p>
                        </Box>
                    )}
                </>
            )}
        </>
    );
};


